import { format, formatDefaultLocale, type FormatLocaleDefinition } from 'd3-format';

// Separators per UI language. Setting the d3 default also covers the charts' scale.tickFormat().
const NUMBER_LOCALES: Record<string, FormatLocaleDefinition> = {
  en: { decimal: '.', thousands: ',', grouping: [3], currency: ['$', ''] },
  fr: { decimal: ',', thousands: '\u202f', grouping: [3], currency: ['', '\u00a0€'], percent: '\u202f%' },
  pt: { decimal: ',', thousands: '.', grouping: [3], currency: ['', '\u00a0€'], percent: '\u00a0%' },
  ar: { decimal: '.', thousands: ',', grouping: [3], currency: ['', ''] }, // Western digits, as in the data
};

let comma = format(',.1f');
let comma0 = format(',d');
let pct1 = format('.1%');

/** Switch separators per UI language; called by the i18n provider before the tree renders. */
export function setNumberLocale(lang: string) {
  const locale = formatDefaultLocale(NUMBER_LOCALES[lang] ?? NUMBER_LOCALES.en!);
  comma = locale.format(',.1f');
  comma0 = locale.format(',d');
  pct1 = locale.format('.1%');
  fmt3 = locale.format(',.3f');
}

/**
 * Format an indicator value:
 * - '%': percentage (e.g. 0.05 -> 5.0%)
 * - 'ratio': numerical ratio / multiple (e.g. 1.75 -> 1.8)
 * - 'per unit' (legacy DB value): percentage
 * - other units: comma-formatted number (unit displayed separately as stored in DB)
 */
export function fmtValue(
  units: string | null | undefined,
  v: number | null | undefined,
  unit?: string | null
): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const effective = unit || units;
  if (effective === '%') return pct1(v);
  if (effective === 'ratio') return comma(v);
  if (effective === 'per unit') return pct1(v);
  return comma(v);
}

/**
 * Value split from its unit, for the `.kpi .val` + `<u>unit</u>` pattern.
 * Units come directly from the database without in-code translation.
 */
export function fmtValueParts(
  units: string | null | undefined,
  v: number | null | undefined,
  unit?: string | null
): { text: string; unit: string } {
  if (v === null || v === undefined || Number.isNaN(v)) return { text: '—', unit: '' };
  const effective = unit || units;
  if (effective === '%') return { text: comma(v * 100), unit: '%' };
  if (effective === 'ratio') return { text: comma(v), unit: 'ratio' };
  if (effective === 'per unit') return { text: comma(v * 100), unit: '%' };
  return { text: comma(v), unit: unitLabel(effective) };
}

/** Units are not translated in code; they are preserved as stored in the database. */
export function unitLabel(units: string | null | undefined): string {
  if (!units) return '';
  if (units === 'per unit') return '%';
  return units;
}

export const fmtInt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : comma0(v));

/** d3 tick specifier for an indicator's units; the scale picks the precision. */
export const tickSpecifier = (units: string | null | undefined, unit?: string | null) => {
  const effective = unit || units;
  if (effective === 'ratio') return ',.1f';
  if (effective === '%' || effective === 'per unit') return '%';
  return ',f';
};

/** 3-decimal value for tables (DT formatRound digits = 3). */
export let fmt3 = format(',.3f');
